<svelte:options
	customElement={{
		tag: 'rf-hero',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	/**
	 * Types
	 */
	export interface ReframeHeroProps {
		id?: string;
		tag: string;
		title?: string;
		subtitle?: string;
		titleTag?: string;
		subtitleTag?: string;
		align: 'left' | 'center' | 'right' | '';
		debug?: boolean;
		responsive?: boolean;
		'no-collection'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import classNames from 'classnames';
	import { v4 as uuidv4 } from 'uuid';
	import RfNotificationCentre from '@core/elements/rf-notification-centre/rf-notification-centre.svelte';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import { getComponentNotifications } from '@store/notification-centre.svelte';
	import parseBool from '@src/utils/bool';
	import { writable } from 'svelte/store';

	/**
	 * Props
	 */
	const {
		id = undefined,
		tag = 'section',
		align = 'left',
		debug = false,
		responsive = true,
		'no-collection': noCollection = false,
	}: ReframeHeroProps = $props();

	/**
	 * Constants
	 */
	export const name = 'rf-hero';
	const componentId = uuidv4();

	/**
	 * Derived
	 */
	const collectionNotifs = $derived(getComponentNotifications(componentId));

	/**
	 * Composables
	 */

	let element = writable(null);

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});
</script>

<svelte:element
	this={tag}
	bind:this={$element}
	class={classNames('rf-hero', {
		'rf-hero--center': align === 'center',
		'rf-hero--right': align === 'right',
		responsive: parseBool(responsive),
	})}
	part="rf-hero"
>
	{#if collectionNotifs && debug}
		<RfNotificationCentre notifications={collectionNotifs} />
	{/if}
	<slot></slot>
</svelte:element>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-hero {
		position: relative;
		display: flex;
		flex-wrap: wrap;
		flex-direction: var(--rf-hero-flex-direction, row);
		font-family: var(--rf-font-family);
		gap: var(--rf-row-gap);
		min-height: 100vh;

		& > * {
			box-sizing: border-box;
		}

		&--center {
			justify-content: center;
		}

		&--right {
			justify-content: right;
		}

		::slotted(rf-text[type='title']) {
			width: calc(100% * var(--rf-hero-title-col, 12) / var(--rf-columns) - var(--rf-col-gap));
		}

		::slotted(rf-text[type='description']) {
			width: calc(100% * var(--rf-hero-subtitle-col, 6) / var(--rf-columns) - var(--rf-col-gap));

			@container rf-hero (width < #{breakpoint.$m}) {
				width: calc(100% * var(--rf-hero-subtitle-col, 12) / var(--rf-columns) - var(--rf-col-gap));
			}
		}

		::slotted(rf-canvas) {
			overflow: hidden;
			--rf-canvas-height: 100vh;
			width: calc(100% * var(--rf-hero-canvas-col, 12) / var(--rf-columns) - var(--rf-col-gap));
		}
		&.responsive {
			::slotted(rf-canvas) {
				@container rf-hero (width < #{breakpoint.$m}) {
					--rf-canvas-height: auto;
				}
			}
		}
	}
</style>
